import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const products = [
  {
    name: "Foundation / BB Cream",
    description: "Your base—choose coverage level based on your preference and skin type.",
    bestFor: "All Skin Types",
    benefits: ["Evens skin tone", "Various coverage levels", "Foundation of any look"]
  },
  {
    name: "Concealer",
    description: "For dark circles, blemishes, and spot coverage.",
    bestFor: "All Skin Types",
    benefits: ["Targeted coverage", "Brightens under-eyes", "Hides imperfections"]
  },
  {
    name: "Mascara",
    description: "Defines eyes and makes lashes look fuller and longer.",
    bestFor: "Everyone",
    benefits: ["Opens up eyes", "Easy impact", "Essential staple"]
  },
  {
    name: "Brow Product",
    description: "Pencil, gel, or pomade—defined brows frame your face.",
    bestFor: "All Brow Types",
    benefits: ["Frames the face", "Polished look", "Multiple formats available"]
  },
  {
    name: "Blush",
    description: "Adds warmth and dimension—cream or powder based on preference.",
    bestFor: "All Skin Types",
    benefits: ["Adds color", "Healthy-looking glow", "Versatile application"]
  },
  {
    name: "Lip Color",
    description: "Lipstick, gloss, or tinted balm—something to complete your look.",
    bestFor: "Everyone",
    benefits: ["Completes the look", "Many finish options", "Express your style"]
  },
  {
    name: "Setting Spray / Powder",
    description: "Helps your makeup last longer throughout the day.",
    bestFor: "All Skin Types",
    benefits: ["Extends wear time", "Controls shine", "Locks in makeup"]
  },
  {
    name: "Makeup Brushes / Sponge",
    description: "Good tools make application easier and more even.",
    bestFor: "Everyone",
    benefits: ["Better application", "Professional results", "Long-lasting investment"]
  }
];

const MakeupEssentials = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="essentials-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/makeup" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Makeup
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Makeup Basics
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Makeup Essentials
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            The core products every makeup bag should have. Whether you're building your first collection or editing down to the essentials, these are the products that matter most.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Building Your Collection
          </h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            You don't need dozens of products to create beautiful looks. A curated collection of quality essentials will serve you better than drawers full of items you never use.
          </p>
          <p className="text-[#374151] leading-relaxed">
            Start with the basics below, then add specialty items as you develop your skills and discover your preferences.
          </p>
        </div>
      </section>

      {/* Products */}
      <section data-testid="essentials-products" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            The Essentials
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {products.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Choose
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Match your foundation:</strong> Test on your jawline in natural light for the best match.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Consider your undertone:</strong> Cool, warm, or neutral affects how colors look on you.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Quality over quantity:</strong> A few well-chosen products beat a drawer full of mediocre ones.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Check expiration dates:</strong> Makeup does expire—mascara every 3 months, foundations 1-2 years.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice variant="inline" />
        </div>
      </section>
    </Layout>
  );
};

export default MakeupEssentials;
